'use client';

import { useEffect, useRef, useState, type ReactElement } from 'react';

import type { VideoGeneratorSimpleProps } from '@/shared/blocks/generator/video-simple';

type LoadedGenerator = (
  props: VideoGeneratorSimpleProps
) => ReactElement | null;

function GeneratorSkeleton() {
  return (
    <section className="pt-8 pb-12 md:pt-9 md:pb-16">
      <div className="container">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-8 lg:h-[calc(100vh-7rem)] lg:min-h-[620px] lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <div className="bg-card border-border/70 space-y-4 rounded-xl border p-5 shadow-sm">
              <div className="bg-muted h-10 w-48 animate-pulse rounded-lg" />
              <div className="bg-muted h-44 animate-pulse rounded-2xl" />
              <div className="bg-muted h-44 animate-pulse rounded-2xl" />
              <div className="bg-muted h-12 animate-pulse rounded-full" />
            </div>
            <div className="bg-card border-border/70 rounded-xl border p-5 shadow-sm">
              <div className="bg-muted h-full min-h-80 animate-pulse rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function VideoGeneratorDeferred(props: VideoGeneratorSimpleProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [Generator, setGenerator] = useState<LoadedGenerator | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || shouldLoad) return;

    if (!('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px', threshold: 0.3 }
    );

    observer.observe(root);
    return () => observer.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    if (!shouldLoad || Generator) return;

    let cancelled = false;
    void import('@/shared/blocks/generator/video-simple').then((mod) => {
      if (!cancelled) {
        setGenerator(() => mod.VideoGeneratorSimple);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [Generator, shouldLoad]);

  return (
    <div ref={rootRef}>
      {Generator ? <Generator {...props} /> : <GeneratorSkeleton />}
    </div>
  );
}
